import React from "react";

import { Chip, Link, Text } from "@performyard/py-components";
import { MeetingExtendedModelJson } from "@performyard/service-contracts";

import AvatarGroup from "../../../components/AvatarGroup";
import ScrollableTable from "../../../components/TableLite/TableLiteScrollable";

import { getFormattedMeetingDateString } from "../utils";

import EmptySearchTableMessage from "../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";

interface CompanyMeetingsTableProps {
	meetings: MeetingExtendedModelJson[];
	isFetchingMeetings: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
	onMeetingNameClick: (meeting: MeetingExtendedModelJson) => void;
}

const CompanyMeetingsTable = (props: CompanyMeetingsTableProps) => {
	const {
		meetings,
		isFetchingMeetings,
		activeSort,
		activeSortDirection,
		handleColumnHeaderClick,
		onMeetingNameClick,
	} = props;

	const getMeetingStatusChip = (status: string) => {
		switch (status) {
			case "active":
				return <Chip label="Active" color="primary" size={20} />;
			case "archived":
				return <Chip label="Archived" color="neutral" size={20} />;
			case "completed":
				return <Chip label="Completed" color="green" size={20} />;
			case "canceled":
				return <Chip label="Canceled" color="red" size={20} />;
			default:
				return <Chip label={status} color="primary" size={20} />;
		}
	};

	const columns = [
		{
			id: "name",
			label: "Meeting Name",
			isSortable: true,
			width: 280,
			getter: (meeting: MeetingExtendedModelJson) => {
				return (
					<div
						className="cursor-pointer text-primary-6 hover:underline"
						onClick={() => onMeetingNameClick(meeting)}
					>
						<div className="py-line-clamp-1" style={{ maxWidth: 250 }}>
							<Text size="paragraph-3" weight="regular">
								{meeting.name}
							</Text>
						</div>
					</div>
				);
			},
		},
		{
			id: "participants",
			label: "Participants",
			isSortable: false,
			width: 100,
			getter: (meeting: MeetingExtendedModelJson) => {
				const employees = meeting.participants?.map((participant) => {
					return { _id: participant.userId, name: participant.name };
				});
				return <AvatarGroup employees={employees} maxLength={2} employeeNameTooltip={true} />;
			},
		},
		{
			id: "meetingWorkspaceName",
			label: "Meeting Workspace Name",
			isSortable: true,
			width: 280,
			getter: (meeting: MeetingExtendedModelJson) => {
				return (
					<Link
						href={`/meeting-workspaces/${meeting.meetingWorkspaceId}`}
						forceHardReload={true}
						underline="hover"
					>
						<div className="py-line-clamp-1" style={{ maxWidth: 250 }}>
							<Text size="paragraph-3" weight="regular">
								{meeting.meetingWorkspaceName || ""}
							</Text>
						</div>
					</Link>
				);
			},
		},
		{
			id: "meetingDate",
			label: "Meeting Date",
			isSortable: true,
			width: 272,
			getter: (meeting: MeetingExtendedModelJson) => {
				return (
					<div className="py-line-clamp-1" style={{ maxWidth: 250 }}>
						<Text size="paragraph-3" weight="regular">
							{getFormattedMeetingDateString(meeting.startDateTime, meeting.endDateTime)}
						</Text>
					</div>
				);
			},
		},
		{
			id: "status",
			label: "Status",
			isSortable: true,
			width: 100,
			getter: (meeting: MeetingExtendedModelJson) => {
				return getMeetingStatusChip(meeting.status);
			},
		},
	];

	return (
		<div data-testid="companyMeetingsContainer">
			<ScrollableTable
				rowKey="_id"
				columns={columns}
				rows={meetings}
				isLoading={isFetchingMeetings}
				activeSort={activeSort}
				activeSortDirection={activeSortDirection}
				onColumnClick={(columnID, nextDirection) =>
					handleColumnHeaderClick({ name: columnID, direction: nextDirection as "asc" | "desc" })
				}
				emptyChildren={<EmptySearchTableMessage mainText="No Meetings Found" />}
				rowClassName="h-[24px] flex items-center"
			/>
		</div>
	);
};

export default CompanyMeetingsTable;
